
import { resolutionSizes } from '@/store/project-store'
import { Handle, ITransformerOptions, ScaleHandle, Transformer as PIXITransformer, TransformerHandle } from '@pixi-essentials/transformer'
import * as PIXI from 'pixi.js'

const SCALE_COMPONENTS: {
  [H in ScaleHandle]: { x: (-1 | 0 | 1); y: (-1 | 0 | 1) };
} = {
  topLeft: { x: -1, y: -1 },
  topCenter: { x: 0, y: -1 },
  topRight: { x: 1, y: -1 },
  middleLeft: { x: -1, y: 0 },
  middleCenter: { x: 0, y: 0 },
  middleRight: { x: 1, y: 0 },
  bottomLeft: { x: -1, y: 1 },
  bottomCenter: { x: 0, y: 1 },
  bottomRight: { x: 1, y: 1 }
}

const tempPoint = new PIXI.Point()
const tempPointer = new PIXI.Point()

const canvasPercentage = 0.05
const minTotalScale = 0.3
const defaultOffset = 50

export enum MinimumScaleType {
  RELATIVE,
  ABSOLUTE
}

export default class Transformer extends PIXITransformer {

  public static ScaleHandles: ScaleHandle[] = [
    'topLeft',
    'topCenter',
    'topRight',
    'middleLeft',
    'middleCenter',
    'middleRight',
    'bottomLeft',
    'bottomCenter',
    'bottomRight'
  ]

  /**
   * Contructor for a extended PIXI Transformer object
   * @param options options for PIXI Transformer contructor
   * @param canvasBounds the canvas area the transformer is placed in, used to get the height and width restrictions
   * @param minScaleType determines how the scaling minimum is done, either by percentage of the object or by a minimum length
   */
  constructor (options?: Partial<ITransformerOptions> | undefined, canvasBounds?: PIXI.Rectangle, minScaleType: MinimumScaleType = MinimumScaleType.ABSOLUTE) {
    super(options)

    // Store the original translateGroup function, required as property functions get overwritten rather than overrided
    const superTranslateGroup = this.translateGroup
    // Store the original scaleGroup function
    const superScaleGroup = this.scaleGroup

    if (canvasBounds) {
      /** Translate the group within the area of the canvas, overwrites the translateGroup property function */
      this.translateGroup = (delta: PIXI.Point) => {
        const bounds = this.getGroupBounds()
        const canvasWidth = canvasBounds.width
        const canvasHeight = canvasBounds.height

        // make half width/height of the square as the movement boundary
        const isLeftEdgeIntersecting = bounds.center.x + delta.x < 0
        const isRightEdgeIntersecting = (bounds.center.x + delta.x) > canvasWidth
        const isTopEdgeIntersecting = bounds.center.y + delta.y < 0
        const isBottomEdgeIntersecting = bounds.center.y + delta.y > canvasHeight

        // Clamp the translation of the transformer to half width/height 
        if (isLeftEdgeIntersecting) {
          delta.x = 0 - bounds.center.x
        } else if (isRightEdgeIntersecting) {
          delta.x = canvasWidth - bounds.center.x
        }

        if (isTopEdgeIntersecting) {
          delta.y = 0 - bounds.center.y
        } else if (isBottomEdgeIntersecting) {
          delta.y = canvasHeight - bounds.center.y
        }

        superTranslateGroup(delta)
      }

      /**
       * Scales the group, overwrites the scaleGroup property function
       * Uses the original scaleGroup code to calculate the delta scale
       * From https://github.com/ShukantPal/pixi-essentials/blob/0190736d7155cbd2e767c26c36a1055768a11498/packages/transformer/src/Transformer.ts#L896
       * Has two limits in place, that will change the delta scale: 
       *   - The contents cannot be scaled off the cavnas
       *   - Overall scaling factor cannot be lower than the minimum scale factor
       * It then reverse calculates the pointerPosition for the new delta scales and pass it into the original scaleGroup
      */
      this.scaleGroup = (handle: ScaleHandle, pointerPosition: PIXI.Point) => {
        /** --------Original code starts-------- */
        this._transformHandle = handle
        this._transformType = 'scale'

        // Directions along x,y axes that will produce positive scaling
        const xDir = SCALE_COMPONENTS[handle].x
        const yDir = SCALE_COMPONENTS[handle].y

        const bounds = this.groupBounds
        const innerBounds = bounds.innerBounds

        // Position of handle in the group's world-space
        const handlePosition = this.worldTransform.apply(this.handleAnchors[handle], tempPoint)
        this.projectionTransform.applyInverse(handlePosition, handlePosition)
        pointerPosition = this.projectionTransform.applyInverse(pointerPosition, tempPointer)

        // Delta vector in world frame
        let dx = pointerPosition.x - handlePosition.x
        let dy = pointerPosition.y - handlePosition.y

        // Unit vector along u-axis (horizontal axis after rotation) of bounds
        const uxvec = (bounds.topRight.x - bounds.topLeft.x) / innerBounds.width
        const uyvec = (bounds.topRight.y - bounds.topLeft.y) / innerBounds.width

        // Unit vector along v-axis (vertical axis after rotation) of bounds
        const vxvec = (bounds.bottomLeft.x - bounds.topLeft.x) / innerBounds.height
        const vyvec = (bounds.bottomLeft.y - bounds.topLeft.y) / innerBounds.height

        // Delta vector in rotated frame of bounds
        let du = (dx * uxvec) + (dy * uyvec)
        let dv = (dx * vxvec) + (dy * vyvec)

        // Scaling factors along x,y axes
        let sx = 1 + (du * xDir / innerBounds.width)
        let sy = 1 + (dv * yDir / innerBounds.height)

        // Only lock aspect ratio if using a handle that scales along both axes.
        const lockAspectRatio = this.lockAspectRatio && (
          handle === 'topLeft' ||
          handle === 'topRight' ||
          handle === 'bottomLeft' ||
          handle === 'bottomRight'
        )

        if (lockAspectRatio) {
          if (sx > sy) {
            sy = sx
          }
          else {
            sx = sy
          }
        }
        /** --------Original code ends-------- */

        const scaleRatio = sx / sy
        const { minSx, minSy } = getScaleLimits(handlePosition, this.getBounds())

        // Clamp the X scale, and adjust Sy to match the change
        sx = Math.max(minSx, sx)
        sy = sx / scaleRatio

        // Clamp the Y scale,  and adjust Sx to match the change
        sy = Math.max(minSy, sy)
        sx = sy * scaleRatio

        if (minScaleType === MinimumScaleType.ABSOLUTE) {
          const newWidth = sx * innerBounds.width
          const newHeight = sy * innerBounds.height

          const minimumSize = resolutionSizes.portrait.width * canvasPercentage
          // Ensure the minimum width is not subceeded
          if (newWidth < minimumSize || newHeight < minimumSize) {
            sx = minimumSize / innerBounds.width
            sy = minimumSize / innerBounds.height
          }

        } else {
          // Scale is the same for each object in the group
          const originalWidth = innerBounds.width / this.group[0].scale.x
          const originalHeight = innerBounds.height / this.group[0].scale.y

          // Overall scaling factor
          const totalScaleX = (sx * innerBounds.width) / originalWidth
          const totalScaleY = (sy * innerBounds.height) / originalHeight

          // Ensure the minimum scaling factor is not exceeded
          if (totalScaleX < minTotalScale || totalScaleY < minTotalScale) {
            sx = minTotalScale * originalWidth / innerBounds.width
            sy = minTotalScale * originalHeight / innerBounds.height
          }
        }

        // Reverse calculate the pointer position for the changed scaling factor 
        du = (sx - 1) * innerBounds.width / xDir
        dv = (sy - 1) * innerBounds.height / yDir

        dx = (vyvec * du - uyvec * dv) / (uxvec * vyvec - uyvec * vxvec)
        dy = (vxvec * du - uxvec * dv) / (vxvec * uyvec - uxvec * vyvec)

        pointerPosition.x = dx + handlePosition.x
        pointerPosition.y = dy + handlePosition.y

        superScaleGroup(handle, pointerPosition)
      }

      /** 
       * Returns the minimum and maximum scaling factors in the x and y axis 
       * 
       * For the x-axis: 
       *  - Right handles: maxSx is derived from the distance between the inner bounds of the transformer to the right edge (enlarged),
       *                   minSx is calculated based on the left edge (shrunk). 
       *  - Left handles: maxSx is derived from the distance to the left edge, minSx is calculated based on the right edge.
       * 
       * For the y-axis:
       *  - Bottom handles: maxSy is derived from the distance to the bottom edge, minSy is calculated based on the top edge.
       *  - Top handles: maxSy is derived from the distance to the top edge, minSy is calculated based on the bottom edge.
       */
      const getScaleLimits = (handlePosition: PIXI.Point, bounds: PIXI.Rectangle) => {
        const isRightHandles = handlePosition.x > this.groupBounds.center.x
        const isBottomHandles = handlePosition.y > this.groupBounds.center.y

        // How far from the edge the boundary of scaling is
        const xOffset = bounds.width < defaultOffset ? bounds.width : defaultOffset
        const yOffset = bounds.height < defaultOffset ? bounds.height : defaultOffset

        const minSx = isRightHandles ? (canvasBounds.left + xOffset - bounds.left) / bounds.width : (bounds.right - canvasBounds.right + xOffset) / bounds.width
        const minSy = isBottomHandles ? (canvasBounds.top + yOffset - bounds.top) / bounds.height : (bounds.bottom - canvasBounds.bottom + yOffset) / bounds.height
        
        return {
          minSx,
          minSy
        }
      }
    }
  }

  protected onPointerDown (e: PIXI.InteractionEvent) {
    // Prevent secondary (right) click movement
    if (e.data.button === 2) {
      return
    }
    super.onPointerDown(e)
  }

  public getHandle (handle: Handle): TransformerHandle | null {
    return this.handles[handle] ?? null
  }
}